import { BadgeVariants } from '@/app/shared/ui/spartan';
import { computePresetRange } from '@/app/shared/ui/components/date-range-selector/date-range-selector.logic';
import { DateRangeValue } from '@/app/shared/ui/components/date-range-selector/date-range-selector.model';
import { EMPTY_VALUE, formatAmount } from '../../utils/hospital-account-display.util';
import { SettlementFilters } from '../models/settlement-query.model';

export interface SettlementStatusPresentation {
  label: string;
  variant: BadgeVariants['variant'];
}

// Ids de CEB_ESTADO_LIQUIDACION.
const SETTLEMENT_STATUS_VARIANTS: Readonly<Record<number, BadgeVariants['variant']>> = {
  1: 'neutral',
  2: 'warning',
  3: 'info',
  4: 'info',
  5: 'info',
  6: 'success',
  7: 'neutral',
  8: 'success',
};

const BONUS_STATUS_PRESENTATION: Readonly<Record<string, SettlementStatusPresentation>> = {
  VALIDO: { label: 'Válido', variant: 'success' },
  'NO VALIDO': { label: 'No válido', variant: 'danger' },
  ANULADO: { label: 'Anulado', variant: 'neutral' },
};

export const toTitleCase = (value: string): string =>
  value.toLowerCase().replace(/(^|\s)\p{L}/gu, (char) => char.toUpperCase());

export const settlementStatusPresentation = (
  statusId: number,
  status: string,
): SettlementStatusPresentation => ({
  label: toTitleCase(status) || EMPTY_VALUE,
  variant: SETTLEMENT_STATUS_VARIANTS[statusId] ?? 'neutral',
});

export const bonusStatusPresentation = (status: string): SettlementStatusPresentation =>
  BONUS_STATUS_PRESENTATION[status.toUpperCase()] ?? {
    label: toTitleCase(status) || EMPTY_VALUE,
    variant: 'neutral',
  };

export const formatOptionalAmount = (amount: number | null): string =>
  amount === null ? EMPTY_VALUE : formatAmount(amount);

export const orDash = (value: string | number | null): string =>
  value === null || value === '' ? EMPTY_VALUE : String(value);

export const sumAmounts = (amounts: ReadonlyArray<number | null>): number =>
  amounts.reduce<number>((sum, amount) => sum + (amount ?? 0), 0);

// El BFF exige al menos un filtro, así que al ingresar se envía un rango de fecha de emisión.
export const DEFAULT_SETTLEMENT_DATE_RANGE: DateRangeValue = {
  preset: 'LAST_THREE_MONTHS',
  startDate: null,
  endDate: null,
};

export const toDateFilters = (
  range: DateRangeValue,
): Pick<SettlementFilters, 'startDate' | 'endDate'> => {
  const { startDate, endDate } = computePresetRange(range.preset) ?? range;
  return { startDate: startDate ?? undefined, endDate: endDate ?? undefined };
};

export const defaultSettlementFilters = (): SettlementFilters =>
  toDateFilters(DEFAULT_SETTLEMENT_DATE_RANGE);

export const validateSettlementFilters = (filters: SettlementFilters): string | null => {
  const { settlementId, statusId, startDate, endDate } = filters;
  if (!!startDate !== !!endDate) return 'Ingresa las fechas de emisión "Desde" y "Hasta".';
  if (startDate && endDate && startDate > endDate) {
    return 'La fecha "desde" no puede ser posterior a "hasta".';
  }
  if (settlementId === undefined && statusId === undefined && !startDate) {
    return 'Ingresa al menos un filtro para buscar.';
  }
  return null;
};
